import type { CustomerTeamMember } from '@clockwise/clients/customer-team';
import type { Customer } from '@clockwise/clients/customers';
import {
  TIMESHEET_STATUSES,
  type Timesheet,
  type TimesheetStatus,
} from '@clockwise/clients/timesheets';
import type { User } from '@clockwise/clients/users';
import {
  findCoveringMembership,
  findCoveringUnlock,
  isoDateString,
  isTimesheetDateLocked,
  type TimesheetLockStage,
  todayInZone,
} from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eraser,
  Loader2,
  MoreHorizontal,
  Plus,
  Search as SearchIcon,
  Settings,
  Table2,
  Undo2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { z } from 'zod';
import { Combobox } from '@/components/custom/combobox';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { RequirePermission } from '@/components/custom/require-permission';
import { MOD_ENTER_KEYS, MOD_KEYSHORTCUT, ShortcutHint } from '@/components/custom/shortcut-hint';
import {
  TimesheetDayList,
  type TimesheetDayListHandle,
} from '@/components/custom/timesheet-day-list';
import {
  type DateRange,
  TimesheetRecallPopover,
} from '@/components/custom/timesheet-recall-popover';
import { TimesheetSubmitButton } from '@/components/custom/timesheet-submit-button';
import {
  parseSubmitFieldErrors,
  type TimesheetSubmitFieldErrors,
} from '@/components/custom/timesheet-submit-errors';
import {
  addDays,
  isEntryLocked,
  mondayOf,
  normalizeIndex,
  partitionWeekForSubmit,
  shortDate,
  sundayOf,
} from '@/components/custom/timesheet-week';
import { TimesheetWeekFindBar } from '@/components/custom/timesheet-week-find-bar';
import {
  TimesheetWeekGrid,
  type TimesheetWeekGridHandle,
} from '@/components/custom/timesheet-week-grid';
import { TimesheetWeekStatusStrip } from '@/components/custom/timesheet-week-status-strip';
import { PageShellHeaderSlot } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useEngagements } from '@/hooks/use-engagements';
import { useUnsavedRouteGuard } from '@/hooks/use-unsaved-route-guard';
import {
  customersApi,
  customerTeamApi,
  timesheetSettingsApi,
  timesheetsApi,
  timesheetUnlocksApi,
  usersApi,
} from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';
import { getCommittedEntryFocus, useCommittedEntryFocus } from '@/lib/timesheet-commit-focus';
import { cn, localTodayIso } from '@/lib/utils';

const VIEW_VALUES = ['weekly', 'day'] as const;
type ViewMode = (typeof VIEW_VALUES)[number];

/**
 * Module-level singleton so the `memberships ?? EMPTY` fallback keeps a
 * stable reference across renders. A fresh `[]` literal on every render
 * would invalidate every downstream useMemo/useCallback that takes
 * memberships as a dep — measurable churn during the loading window
 * with many timesheet rows.
 */
const EMPTY_MEMBERSHIPS: readonly CustomerTeamMember[] = Object.freeze([]);

const search = z.object({
  view: z.enum(VIEW_VALUES).optional(),
  week: isoDateString().optional(),
  day: isoDateString().optional(),
  // Bookmarkable week-view find. `find` is the free-text query (customer or
  // activity name); `findStatus` is a comma-separated status scope
  // (e.g. "rejected,failed"). A link from a rejected/approved email can deep-
  // link straight into the filtered, navigable state via these.
  find: z.string().optional(),
  findStatus: z.string().optional(),
  // Bookmarkable target user. Defaults to "me" when absent. When the
  // value differs from the logged-in user we're in delegation mode: the
  // page renders the indigo treatment, mutations target this id, and the
  // customer dropdown / per-day enable map scope to this user.
  userId: z.string().uuid().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_authenticated/timesheets/')({
  validateSearch: search,
  component: TimesheetsPage,
});

function TimesheetsPage() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useSession();
  const email = session.data?.user.email ?? '';
  const { user: me, loading: meLoading } = useMe();

  // URL state derives the week. Day view uses the `day` param (clamped
  // into the displayed week). When only `day` is supplied (e.g. a shared
  // deep link or a stale bookmark into a past week), fall back to that day's
  // week — otherwise the range query would load today's week and the day
  // would render empty even though its entries exist.
  const view: ViewMode = params.view ?? 'weekly';
  const weekStart = mondayOf(params.week ?? params.day ?? localTodayIso());
  const weekEnd = sundayOf(weekStart);
  const day = params.day ?? clampToWeek(localTodayIso(), weekStart);
  const isCurrentWeek = weekStart === mondayOf(localTodayIso());

  // The target user — who the page is showing the timesheet FOR. When
  // params.userId is missing it's the caller themselves. We don't
  // mutate the URL on the no-op self case; switching from someone-else
  // back to self drops the param via stripDefaults.
  const targetUserId = params.userId ?? me?.id ?? null;
  const isDelegating = Boolean(me && targetUserId && targetUserId !== me.id);

  const updateParams = (next: Partial<Search>) =>
    navigate({
      search: (prev: Search) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // Fetch the target user record when we're delegating. Self-mode reuses
  // the `me` row from useMe(); avoids a redundant network round-trip on
  // the common path.
  const targetUserQuery = useQuery({
    queryKey: queryKeys.users.detail(targetUserId ?? ''),
    queryFn: () => usersApi.get(targetUserId as string),
    enabled: Boolean(targetUserId && isDelegating),
  });
  const targetUser: User | null = isDelegating ? (targetUserQuery.data?.data ?? null) : me;

  // Picker source: who am I allowed to submit time for in this week?
  // Always includes self. Picker only renders when length > 1.
  const submittableQuery = useQuery({
    queryKey: queryKeys.users.submittableByMe({ fromDate: weekStart, toDate: weekEnd }),
    queryFn: () => usersApi.submittableByMe({ fromDate: weekStart, toDate: weekEnd }),
    enabled: Boolean(me?.id),
    staleTime: 60_000,
  });
  const submittableUsers = submittableQuery.data?.data ?? [];
  // The picker is for switching to *other* people; exclude self from the list.
  const pickableUsers = me ? submittableUsers.filter((u) => u.id !== me.id) : submittableUsers;

  // Target user's customer-team memberships overlapping the week. Drives
  // both the customer dropdown (unique customerIds) and the per-day
  // disable map (membership window per row).
  const teamMembershipsQuery = useQuery({
    queryKey: queryKeys.customerTeam.forUser({
      teamUserId: targetUser?.id ?? '',
      fromDate: weekStart,
      toDate: weekEnd,
    }),
    queryFn: () =>
      customerTeamApi.listForUser({
        teamUserId: targetUser?.id as string,
        fromDate: weekStart,
        toDate: weekEnd,
      }),
    enabled: Boolean(targetUser?.id),
    staleTime: 60_000,
  });
  // useMemo so the empty fallback keeps a stable identity while the query
  // is loading/refetching/disabled — otherwise a fresh `[]` literal each
  // render churns every downstream useMemo/useCallback that takes
  // `memberships` as a dep (rolesByCustomer in the week grid,
  // roleIdsForEntry in the day list, isOnTeamForDay below).
  const memberships: readonly CustomerTeamMember[] = useMemo(
    () => teamMembershipsQuery.data?.data ?? EMPTY_MEMBERSHIPS,
    [teamMembershipsQuery.data],
  );

  // Engagement (SOW) coverage for the whole visible week — one request, shared
  // by every row. Fetched here rather than in the grid so the day view can
  // consume the same cache entry when it grows an engagement affordance too.
  const engagements = useEngagements(targetUserId ?? undefined, weekStart, weekEnd);

  // Resolve the unique team customers to full Customer rows. Single batched
  // call — N parallel detail fetches would be wasteful when the typical week
  // has 1–5 customers per user. This is the ALL-statuses set; it resolves the
  // label + isInternal of rows already bound to a customer, including one that
  // has since been deactivated (deactivation doesn't end team membership).
  const teamCustomerIds = useMemo(
    () => Array.from(new Set(memberships.map((m) => m.customerId))),
    [memberships],
  );
  const teamCustomersQuery = useQuery({
    queryKey: queryKeys.customers.byIds(teamCustomerIds),
    queryFn: () => customersApi.findByIds(teamCustomerIds),
    enabled: teamCustomerIds.length > 0,
    staleTime: 60_000,
  });
  const teamCustomers: Customer[] = teamCustomersQuery.data?.data ?? [];

  // The customers the dropdown may OFFER: same team set, but the server drops
  // anything inactive (findActiveByIds) so the picker never lets a user start
  // logging against a deactivated customer. Kept separate from `teamCustomers`
  // above — that one must still carry inactive rows for label resolution.
  const selectableCustomersQuery = useQuery({
    queryKey: queryKeys.customers.activeByIds(teamCustomerIds),
    queryFn: () => customersApi.findActiveByIds(teamCustomerIds),
    enabled: teamCustomerIds.length > 0,
    staleTime: 60_000,
  });
  const selectableCustomers: Customer[] = selectableCustomersQuery.data?.data ?? [];

  // Per-day "on the team" predicate. Half-open `[effectiveFrom, effectiveTo)`
  // semantics live in `findCoveringMembership` in @clockwise/shared — the
  // same helper the three picker views use, so this predicate stays in
  // lockstep with picker scoping. Memoized so referential equality holds
  // across renders.
  const isOnTeamForDay = useCallback(
    (customerId: string, date: string): boolean =>
      findCoveringMembership(memberships, customerId, date) !== null,
    [memberships],
  );

  // Single query for the whole week; both views render from it.
  const rangeQuery = useQuery({
    queryKey: targetUser
      ? queryKeys.timesheets.range({
          userId: targetUser.id,
          fromDate: weekStart,
          toDate: weekEnd,
        })
      : ['timesheets', 'range', 'unresolved'],
    queryFn: () =>
      timesheetsApi.range({
        userId: (targetUser as User).id,
        fromDate: weekStart,
        toDate: weekEnd,
      }),
    enabled: Boolean(targetUser?.id),
  });
  const entries: Timesheet[] = rangeQuery.data?.data ?? [];

  // Tenant lock settings drive per-date edit/submit/recall gating. The GET
  // is allowed for anyone with timesheet.view (this page), so it's always
  // enabled. The server is the authoritative backstop — this just mirrors
  // the lock into the UI so locked cells render read-only.
  const settingsQuery = useQuery({
    queryKey: queryKeys.timesheetSettings.current(),
    queryFn: () => timesheetSettingsApi.get(),
  });
  const lockState = settingsQuery.data?.data ?? null;
  // The lock boundary closes at the timesheet OWNER's local midnight — the
  // target user (whose sheet this is), not the viewer. Mirrors the backend
  // gate, which evaluates the owner's `users.timezone`.
  //
  // `ownerTimeZone` is null until the target user has loaded; we don't fall
  // back to UTC, because 'UTC' is a real zone we couldn't distinguish from
  // "still loading" — evaluating the boundary against the wrong zone would
  // briefly mislabel cells right at midnight. While either the settings or
  // the owner is unresolved, `isDateLocked` returns false (the server is the
  // authoritative backstop; a stale-open cell just 422s on save).
  const ownerTimeZone = targetUser?.timezone ?? null;

  // Active admin unlocks covering the target user this week — an override
  // that re-opens otherwise-locked cells. The endpoint already scopes to
  // this user + range and filters to active (non-revoked, not past) rows in
  // the owner's timezone, so the client just matches scope per cell with the
  // shared `findCoveringUnlock`.
  //
  // Fetched in PARALLEL with `settingsQuery` (gated only on the target user,
  // not on `lockState.lockEnabled`). Serializing it behind the settings query
  // used to make the unlocks land *after* the grid had already painted, so
  // locked-then-unlocked cells visibly flipped. Running both together means
  // lock state is known by first paint. The cost is one lightweight, 60s-
  // cached request on tenants with locking off — where every date is unlocked
  // anyway, so the result just isn't consulted.
  const unlocksQuery = useQuery({
    queryKey: targetUser
      ? queryKeys.timesheetUnlocks.activeForUser({
          userId: targetUser.id,
          fromDate: weekStart,
          toDate: weekEnd,
        })
      : ['timesheetUnlocks', 'activeForUser', 'unresolved'],
    queryFn: () =>
      timesheetUnlocksApi.listActiveForUser({
        userId: (targetUser as User).id,
        fromDate: weekStart,
        toDate: weekEnd,
      }),
    enabled: Boolean(targetUser?.id),
    staleTime: 60_000,
  });
  const activeUnlocks = unlocksQuery.data?.data ?? [];
  // Until the unlocks query has settled we can't tell a genuinely-locked date
  // from one an override re-opens, so `isDateLocked` treats every date as
  // unlocked meanwhile (below). `isFetched` flips true on success OR error, so
  // an errored fetch still releases the gate — cells then fall back to the raw
  // period lock (the conservative, server-consistent state).
  const unlocksSettled = unlocksQuery.isFetched;

  // Whether `(customerId, date)` is locked for the target user. A date whose
  // period has locked is re-opened when an active unlock covers the exact
  // `(owner, customer, date)` scope — matching the server gate, so a
  // customer-scoped unlock only frees that customer's cells (no date-level
  // over-unlock that would look editable then 422 on save). `customerId` is
  // null for internal time and for not-yet-bound new-entry cells; a
  // customer-specific unlock won't free those, which is the conservative
  // (server-consistent) choice.
  const isDateLocked = useCallback(
    (date: string, customerId: string | null, stage: TimesheetLockStage = 'submission') => {
      if (!lockState || !ownerTimeZone || !targetUser) return false;
      const asOf = todayInZone(ownerTimeZone);
      // `stage` defaults to SUBMISSION, the earlier and stricter boundary, so
      // a caller that forgets it can only ever be conservative. Callers pass
      // `'settlement'` only where the action really is a settlement one —
      // correcting or resubmitting a `rejected`/`failed` row — which
      // `isEntryLocked` derives from the row's status via the same
      // `timesheetLockStageFor` the server enforces with.
      if (!isTimesheetDateLocked(lockState, date, asOf, stage)) return false;
      // Period is locked — but don't paint the lock until we know whether an
      // override re-opens it, or a covered cell would flash locked and then
      // clear once the unlocks arrive. Defaulting to unlocked here (over
      // holding a skeleton) keeps the grid interactive immediately; the only
      // remaining transition is the gentler unlocked→locked one, and it lands
      // early because the unlocks fetch runs in parallel with the settings.
      if (!unlocksSettled) return false;
      return findCoveringUnlock(activeUnlocks, targetUser.id, customerId, date, asOf) === null;
    },
    [lockState, ownerTimeZone, targetUser, activeUnlocks, unlocksSettled],
  );
  // Editable entries on a locked date can't be submitted; submitted entries
  // on a locked date can't be recalled (full freeze). Gates the Submit /
  // Recall affordances below; the per-cell read-only state lives in the grid.
  // Whether ANY submitted entry can still be recalled — not whether none is
  // locked. `recallRange` filters locked rows instead of refusing the batch,
  // so a week straddling a closed and an open period must keep the affordance
  // for its open days; the popover itself greys out the closed ones. Recall is
  // judged on the submission boundary in every status, which is the default
  // stage here.
  const hasRecallableSubmitted = useMemo(
    () => entries.some((e) => e.status === 'submitted' && !isDateLocked(e.date, e.customerId)),
    [entries, isDateLocked],
  );
  // When every day in the visible week is locked, Add row / Copy last week
  // would only spawn immediately read-only cells (and Copy would 422 on the
  // server). Gate both on the whole-week lock. `null` customer mirrors a
  // not-yet-bound new row; an all-customer unlock still re-opens it via
  // findCoveringUnlock, so an unlocked week stays interactive.
  const weekFullyLocked = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).every((d) =>
        isDateLocked(d, null),
      ),
    [weekStart, isDateLocked],
  );

  // Per-day status is now rendered by <TimesheetWeekStatusStrip>; the page
  // needs the gating booleans plus the per-status counts so the Submit
  // tooltip can disclose how many entries the click will actually flip
  // (vs. how many are already submitted on the same week).
  // Submit partition (what flips, what's skipped, what's already in) lives in a
  // pure, unit-tested helper — `partitionWeekForSubmit` — that keys "carries
  // hours" off the SAME `hasLoggedHours` predicate the API's submitRange uses,
  // so the button count and the "N empty skipped" toast can't drift from what
  // the server actually submits.
  const submitPartition = useMemo(
    // The lock predicate is passed in so the count matches what `submitRange`
    // will really take: rows whose period has closed for them are FILTERED by
    // the server, not fatal, so they must not be counted here either.
    () => partitionWeekForSubmit(entries, { isLocked: (e) => isEntryLocked(e, isDateLocked) }),
    [entries, isDateLocked],
  );
  // The mod+S shortcut re-checks the same gate the button uses, and the submit
  // payload carries the empty count for the "N skipped" toast.
  const { hasSubmittableInRange, emptyEditableCount } = submitPartition;

  const queryClient = useQueryClient();
  // Field-level errors raised by the most recent submit attempt, keyed by
  // timesheet id. Set from `ValidationError.fields` in onError below and
  // threaded into TimesheetWeekGrid so cells can highlight inline. The
  // grid clears its own entry as soon as the user starts editing again.
  const [submitFieldErrors, setSubmitFieldErrors] = useState<TimesheetSubmitFieldErrors>({});

  // Count of clearable-empty draft cells (each an unlocked no-hours entry) the
  // week grid is showing — pushed up by the grid, since the cell grouping lives
  // there, not on this flat entry list. Drives the toolbar's "Clear empty
  // entries" button (shown only when there's something to clear).
  // `clearEmptyOpen` gates its confirm.
  const [emptyEntryCount, setEmptyEntryCount] = useState(0);
  const [clearEmptyOpen, setClearEmptyOpen] = useState(false);

  // ── Week-view find (browser-Cmd+F-style) ──
  // The status-scope + text query live in the URL (bookmarkable, email-linkable);
  // the grid computes the ordered matches (it owns sort order + resolved names)
  // and the page drives navigation through the grid's imperative handle.
  // The text query is LOCAL state (seeded from the URL) so typing stays snappy —
  // a URL-controlled input drops fast keystrokes while navigation settles. It's
  // mirrored back to the `find` URL param (debounced, below) for bookmarkability.
  const [findQuery, setFindQueryState] = useState(() => params.find ?? '');
  const findStatuses = useMemo<ReadonlySet<TimesheetStatus>>(() => {
    const raw = params.findStatus ?? '';
    return new Set(
      raw
        .split(',')
        .filter((s): s is TimesheetStatus => (TIMESHEET_STATUSES as readonly string[]).includes(s)),
    );
  }, [params.findStatus]);
  // A scope is active (and the grid dims/highlights) only when there's a query
  // or a selected status.
  const findActive = findQuery.trim().length > 0 || findStatuses.size > 0;

  // Per-status entry counts for the find bar's status dots, scoped to the
  // current find TEXT query. Computed and pushed up by the grid
  // (`onFindStatusCountsChange`) because the text match needs the grid's batched
  // customer/activity name maps — so the dots track the text-filtered subset and
  // a status the query has emptied drops to zero (and the bar hides it). Seeded
  // to all-zero: the grid pushes the real totals on mount (it's rendered
  // whenever the weekly toolbar — and thus the bar — is), before the user can
  // open the bar.
  const [statusCounts, setStatusCounts] = useState<Record<TimesheetStatus, number>>(() => ({
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
    failed: 0,
  }));

  // Whether the find bar is expanded. At rest the toolbar shows only a search
  // icon; opening it reveals the one inline bar with the text field + status
  // dots + stepper. Open by default when a bookmark/email link carried a scope.
  const [findOpen, setFindOpen] = useState(() => Boolean(params.find || params.findStatus));
  const [findIndex, setFindIndex] = useState(0);
  // Live match count, pushed up from the grid (it computes matches). Kept in
  // state so the bar's "N of M" doesn't read the grid ref mid-render.
  const [findMatchCount, setFindMatchCount] = useState(0);
  // Whether a match popover has been opened yet this cursor run. Browser-find
  // semantics: with fresh inputs the counter reads "1 of N" but nothing is open;
  // the FIRST prev/next opens the current match, and only subsequent ones step.
  // A ref (not state) so `stepFind` stays identity-stable and reads it live.
  const findOpenedRef = useRef(false);
  // Auto-open the first match ONCE when arriving via a bookmark/email link that
  // carried find params — so the user lands directly on the entry. Seeded true
  // (i.e. "already done") when no find params were present at mount, so manual
  // in-app filtering never auto-opens a popover.
  const didAutoOpenRef = useRef(!(params.find || params.findStatus));

  // Reset the cursor to the first match, unopened — used whenever the inputs
  // change (new query / status scope), so the next step re-opens from the top.
  const resetFindCursor = useCallback(() => {
    setFindIndex(0);
    findOpenedRef.current = false;
  }, []);

  const setFindQuery = (q: string) => {
    setFindQueryState(q);
    resetFindCursor();
  };

  const toggleFindStatus = (status: TimesheetStatus) => {
    const next = new Set(findStatuses);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    updateParams({ findStatus: next.size ? [...next].join(',') : undefined });
    resetFindCursor();
  };

  // Collapse the whole bar back to the icon and clear the (bookmarkable) scope.
  const closeFind = () => {
    setFindOpen(false);
    setFindQueryState('');
    updateParams({ find: undefined, findStatus: undefined });
    resetFindCursor();
  };

  // Step to a match and open its cell popover. The first step of a cursor run
  // opens the CURRENT match (index unchanged); later steps advance by
  // `direction`, wrapping. The grid clamps the index, so a stale value after an
  // edit still lands on a real match.
  const stepFind = useCallback((direction: 1 | -1) => {
    const count = gridRef.current?.getMatchCount() ?? 0;
    if (count === 0) return;
    setFindIndex((prev) => {
      const base = normalizeIndex(prev, count);
      const next = findOpenedRef.current ? normalizeIndex(base + direction, count) : base;
      findOpenedRef.current = true;
      gridRef.current?.openMatchAt(next);
      return next;
    });
  }, []);

  // Matches are week + view specific, so reset the cursor when either changes;
  // the URL scope itself persists (stepping the same status across weeks is
  // intentional).
  useEffect(() => {
    resetFindCursor();
  }, [weekStart, view, resetFindCursor]);

  // Mirror the (local, snappy) text query back to the `find` URL param so the
  // search stays bookmarkable — debounced so typing doesn't spam history.
  useEffect(() => {
    if ((params.find ?? '') === findQuery) return;
    const t = setTimeout(() => updateParams({ find: findQuery || undefined }), 350);
    return () => clearTimeout(t);
  }, [findQuery, params.find]);

  // Deep-link auto-open: once matches are available for a bookmarked find, open
  // the first one. Fires at most once (guarded by the ref seeded above).
  useEffect(() => {
    if (didAutoOpenRef.current || !findActive || findMatchCount === 0) return;
    didAutoOpenRef.current = true;
    stepFind(1);
  }, [findActive, findMatchCount, stepFind]);

  const submitMutation = useMutation({
    // userId is passed THROUGH the mutationFn arg rather than closed over —
    // if the user switches target between mutate() and the request
    // actually firing, we still hit the intended user. Mirrors the
    // recallMutation shape below; the underlying TanStack Query queue is
    // first-come-first-served so the snapshot is the right value.
    mutationFn: ({
      userId,
      from,
      to,
    }: {
      userId: string;
      from: string;
      to: string;
      // Blank (no-hours) editable rows the server will skip, snapshotted at
      // mutate-time so the toast can disclose them (see onSuccess). Passed
      // through rather than closed over to dodge a stale-closure read.
      emptyCount: number;
    }) => timesheetsApi.submitRange(userId, { fromDate: from, toDate: to }),
    onSuccess: (res, { emptyCount }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      setSubmitFieldErrors({});
      const n = res.data.length;
      // No trailing "for the week" — with partial submission the click can
      // flip a subset; "Submitted N entries." is accurate for both the
      // whole-week and partial cases. When copy-last-week left blank rows
      // behind, disclose that they were skipped so the count isn't a mystery.
      const base = `Submitted ${n} entr${n === 1 ? 'y' : 'ies'}.`;
      toast.success(
        emptyCount > 0
          ? `${base} ${emptyCount} empty entr${emptyCount === 1 ? 'y' : 'ies'} skipped.`
          : base,
      );
    },
    onError: (err) => {
      // Field errors come back on VALIDATION_ERROR with `entries[<id>].<field>`
      // paths — parse them into a map the grid keys on. Top-level message
      // still surfaces as a toast (G12: inline + summary, not toast-only).
      const fieldErrors = parseSubmitFieldErrors(err);
      setSubmitFieldErrors(fieldErrors);
      toast.error(err instanceof Error ? err.message : 'Submit failed');
    },
  });

  // Recall reverses submitRange: every `submitted` row in the chosen
  // range(s) flips back to `draft` and its in-flight approval instance is
  // canceled. The popover lets the user pick a subset of days; we fire one
  // `recallRange` per contiguous range so a non-contiguous selection (Mon +
  // Wed skipping Tue) does NOT incidentally pull Tue if it's also
  // submitted.
  const recallMutation = useMutation({
    // userId snapshotted at mutate-time (passed through args) instead of
    // closed over — if the target user switches between popover-confirm
    // and the network call, we still recall on the user we intended.
    mutationFn: async ({ userId, ranges }: { userId: string; ranges: DateRange[] }) => {
      if (ranges.length === 0) {
        return { recalled: 0, errors: [] as string[] };
      }
      const results = await Promise.allSettled(
        ranges.map((r) =>
          timesheetsApi.recallRange(userId, {
            fromDate: r.fromDate,
            toDate: r.toDate,
          }),
        ),
      );
      let recalled = 0;
      const errors: string[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled') recalled += r.value.data.length;
        else errors.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
      }
      return { recalled, errors };
    },
    onSuccess: ({ recalled, errors }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      // Recall clears any prior submit-time field errors — the rows are
      // editable drafts again.
      setSubmitFieldErrors({});
      if (recalled === 0 && errors.length === 0) return;
      if (errors.length > 0) {
        toast.warning(
          `Recalled ${recalled} entr${recalled === 1 ? 'y' : 'ies'} · ${errors.length} range${
            errors.length === 1 ? '' : 's'
          } failed`,
          { description: errors[0] ?? 'See devtools for details.' },
        );
      } else {
        toast.success(
          `Recalled ${recalled} entr${recalled === 1 ? 'y' : 'ies'}. Submit again when ready.`,
        );
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Recall failed'),
  });

  // Self-heal field errors as the user fixes them. When an entry now has
  // a value for the field that was flagged (or the entry was deleted),
  // drop that key. Avoids the user seeing red on a cell they've already
  // filled in. Re-running the submit still sets a fresh map.
  useEffect(() => {
    setSubmitFieldErrors((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      let changed = false;
      const next: TimesheetSubmitFieldErrors = {};
      for (const id of ids) {
        const errs = prev[id];
        if (!errs) continue;
        const entry = entries.find((e) => e.id === id);
        if (!entry) {
          changed = true;
          continue;
        }
        const filtered: {
          customerId?: string;
          productId?: string;
          hours?: string;
          description?: string;
        } = {};
        if (errs.customerId && entry.customerId == null) filtered.customerId = errs.customerId;
        else if (errs.customerId) changed = true;
        if (errs.productId && entry.productId == null) filtered.productId = errs.productId;
        else if (errs.productId) changed = true;
        if (errs.hours && entry.hours == null) filtered.hours = errs.hours;
        else if (errs.hours) changed = true;
        if (errs.description && !entry.description?.trim()) filtered.description = errs.description;
        else if (errs.description) changed = true;
        if (filtered.customerId || filtered.productId || filtered.hours || filtered.description)
          next[id] = filtered;
      }
      return changed ? next : prev;
    });
  }, [entries]);

  // Copy last week's rows into THIS week with hours blanked. One call: the
  // server owns the planning (which weekday a row lands on, what to skip and
  // why) because every rule in it — on-team, has-hours, don't overwrite — is
  // a rule it enforces on the write anyway. Planning it here meant fetching
  // the user's memberships just to mirror that, and a click landing before
  // that fetch resolved read as "on no teams" and silently copied nothing.
  const copyLastWeekMutation = useMutation({
    mutationFn: async () => {
      if (!targetUser) {
        throw new Error('Could not load your user — refresh and try again.');
      }
      const res = await timesheetsApi.copyWeek(targetUser.id, { weekStart });
      return res.data;
    },
    onSuccess: ({ copied, failed, duplicate, offTeam, incomplete, zeroHours, errors }) => {
      if (!targetUser) return;
      // Surgical invalidate — the range query is the source of truth for
      // every view of this week. Broader keys would refetch unrelated
      // weeks the user hasn't looked at.
      queryClient.invalidateQueries({
        queryKey: queryKeys.timesheets.range({
          userId: targetUser.id,
          fromDate: weekStart,
          toDate: weekEnd,
        }),
      });

      const skipParts: string[] = [];
      if (duplicate > 0) skipParts.push(`${duplicate} already filled`);
      if (offTeam > 0) skipParts.push(`${offTeam} off-team`);
      if (zeroHours > 0) skipParts.push(`${zeroHours} no hours`);
      if (incomplete > 0) skipParts.push(`${incomplete} incomplete`);

      if (copied === 0 && failed === 0) {
        if (skipParts.length === 0) {
          toast.info('Nothing to copy — last week was empty.');
        } else {
          // copied=0 with skips is informational, not a success — the
          // user clicked Copy and got no new rows.
          toast.info(`Nothing copied · ${skipParts.join(' · ')}`);
        }
        return;
      }

      const parts = [`Copied ${copied} cell${copied === 1 ? '' : 's'}`, ...skipParts];
      if (failed > 0) parts.push(`${failed} failed`);
      const message = parts.join(' · ');
      if (failed > 0) {
        toast.warning(message, {
          description: errors[0] ?? 'See devtools for details.',
        });
      } else {
        toast.success(message);
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Copy failed'),
  });

  // The indigo "viewing on behalf of" treatment activates iff we're
  // showing someone else's timesheet. Indigo (not amber) to keep clear
  // of the under-target delta and rejected-status amber elsewhere on
  // the page.
  const viewingOnBehalfOf: User | null = isDelegating ? targetUser : null;

  const displayUser = targetUser ?? me;
  const initials = (
    displayUser
      ? `${displayUser.firstName[0] ?? ''}${displayUser.lastName[0] ?? ''}`
      : (email[0] ?? '?')
  )
    .toUpperCase()
    .slice(0, 2);
  const displayName = displayUser
    ? `${displayUser.firstName} ${displayUser.lastName}`.trim()
    : email;

  // The grid owns the "draft row" state; the page header drives `+ Add row`
  // via this imperative handle so the button can sit next to the week pager
  // instead of inside the table card.
  const gridRef = useRef<TimesheetWeekGridHandle>(null);
  // Day view has no Add-row button; the shortcut focuses its blank draft card.
  const dayListRef = useRef<TimesheetDayListHandle>(null);

  // ── Reveal a row produced by committing a timer ───────────────────────────
  // The global timer popover commits outside this page's tree and broadcasts
  // the produced entry through a module-level signal. We hop to that entry's
  // week/day (if needed) and scroll its row into view once the invalidation-
  // driven refetch renders it — so the user never has to hunt for it manually.
  const committedFocus = useCommittedEntryFocus();
  // Holds the entry id awaiting a scroll, across the navigate → refetch gap.
  const focusEntryRef = useRef<string | null>(null);
  // Baseline the seq at mount so a commit from an earlier visit (still sitting
  // in the singleton) can't hijack this page load — only strictly-newer signals
  // act.
  const handledFocusSeqRef = useRef(getCommittedEntryFocus()?.seq ?? 0);

  // A fresh commit arrives → remember the row and move to its week/day. The
  // committed entry belongs to the signed-in user, so when delegating (viewing
  // someone else's sheet) it isn't in range — skip rather than disrupt.
  useEffect(() => {
    if (!committedFocus || committedFocus.seq <= handledFocusSeqRef.current) return;
    handledFocusSeqRef.current = committedFocus.seq;
    if (isDelegating) return;
    focusEntryRef.current = committedFocus.entryId;
    const targetWeek = mondayOf(committedFocus.date);
    // updateParams is intentionally omitted from deps (recreated each render,
    // closes over the stable `navigate`) — matching the convention elsewhere
    // on this page.
    if (targetWeek !== weekStart) {
      updateParams(
        view === 'day' ? { week: targetWeek, day: committedFocus.date } : { week: targetWeek },
      );
    } else if (view === 'day' && day !== committedFocus.date) {
      updateParams({ day: committedFocus.date });
    }
  }, [committedFocus, isDelegating, weekStart, view, day]);

  // Switching to a delegated sheet drops any pending commit-focus: the entry is
  // the signed-in user's, so it isn't in the delegate's range. Without this the
  // ref lingers and re-fires on return to self — an unexpected scroll to a stale
  // entry committed before the detour.
  useEffect(() => {
    if (isDelegating) focusEntryRef.current = null;
  }, [isDelegating]);

  // Once the target row is rendered (refetch landed, on the right week/day),
  // scroll it into view and clear the pending target. Re-runs as `entries`
  // updates, so the in-flight refetch that produces the row triggers the scroll.
  useEffect(() => {
    const id = focusEntryRef.current;
    if (!id) return;
    const target = entries.find((e) => e.id === id);
    if (!target) return; // not in this week's range yet — retry on next render
    if (view === 'day' && target.date !== day) return; // waiting on the day hop
    const handle = view === 'day' ? dayListRef.current : gridRef.current;
    if (handle?.scrollEntryIntoView(id)) focusEntryRef.current = null;
  }, [entries, view, day]);

  // Warn before navigating AWAY from the timesheet with a started-but-empty
  // draft row (a customer/activity picked but no hours): such rows aren't
  // persisted and would be silently lost. Read live off the grid's imperative
  // handle on each navigation attempt.
  const hasUnsavedDraftRows = useCallback(
    () => gridRef.current?.hasUnsavedDraftRows() ?? false,
    [],
  );
  const { blockerDialog } = useUnsavedRouteGuard(
    // Prompt whenever the next location would stop rendering the weekly grid:
    // a real page-leave (different route) OR switching to day view on the same
    // route. Both unmount the grid, which discards the draft (it lives in
    // grid-local state). Week paging and the userId switch keep the weekly grid
    // mounted, so they pass through unblocked.
    ({ current, next }) => {
      if (!hasUnsavedDraftRows()) return false;
      const nextKeepsWeeklyGrid =
        next.routeId === current.routeId && (next.search as Search).view !== 'day';
      return !nextKeepsWeeklyGrid;
    },
    {
      title: 'Leave with unsaved rows?',
      description:
        'Some rows have a customer or activity selected but no hours entered. Empty rows aren’t saved and will be discarded if you leave.',
    },
    // Tab close / refresh has no in-app destination, so the native prompt is
    // the only guard there. Gate it on actual drafts — this is a high-traffic
    // landing page, so an unconditional "Leave site?" on every refresh would
    // be noise.
    hasUnsavedDraftRows,
  );

  // The "Switch user" link is a closed affordance by default — keeps the
  // header clean for the common self-view case. Clicking the link opens
  // a Combobox in its place, which auto-opens its dropdown for typeahead
  // search and reverts to the link on selection or click-away.
  const [pickerOpen, setPickerOpen] = useState(false);

  const onPickUser = useCallback(
    (u: User | null) => {
      if (!u) return;
      // Drop the userId param when picking self so the URL is clean for
      // the common case; otherwise pin to the picked user.
      updateParams({ userId: me && u.id === me.id ? undefined : u.id });
    },
    // updateParams is stable inside the function above; me id triggers
    // the self-vs-other comparison.
    [me?.id],
  );

  // Picker only renders when there's at least one user other than self.
  // Saves visual noise on the common "I am not a team lead" path.
  const showPicker = submittableUsers.length > 1;
  // Submit button only renders in week view. Day view is intentionally a
  // draft-entry surface — a week submit is one transaction over Mon–Sun
  // and we don't want a day-scoped affordance that can produce a partial
  // submitted/draft state.
  const showSubmitWeek = view !== 'day';

  // Keyboard shortcuts (react-hotkeys-hook): `mod` = ⌘ on macOS, Ctrl elsewhere.
  // `enableOnFormTags` lets them fire while a cell/description input is focused;
  // `preventDefault` stops the browser's "save page" dialog on mod+s. Each
  // handler re-checks the exact enable/lock guard its button uses, so a shortcut
  // can never trigger an action the button wouldn't allow.
  //
  // mod+Enter — add a row (week view) or focus the new-entry card (day view).
  useHotkeys(
    'mod+enter',
    () => {
      if (!targetUser) return;
      if (view === 'day') {
        dayListRef.current?.focusNewEntry();
      } else if (!weekFullyLocked) {
        gridRef.current?.addRow();
      }
    },
    { preventDefault: true, enableOnFormTags: true },
    [targetUser, view, weekFullyLocked],
  );
  // mod+S — submit the week (week view only); day view points to where submit lives.
  useHotkeys(
    'mod+s',
    () => {
      if (view === 'day') {
        toast.info('Switch to week view to submit your week.');
        return;
      }
      // `hasSubmittableInRange` already excludes rows whose period has closed
      // for them, so it is the whole gate — a week that straddles an open and
      // a closed period is still submittable for its open half, exactly as
      // `submitRange` treats it.
      const canSubmit = !!targetUser && !submitMutation.isPending && hasSubmittableInRange;
      if (canSubmit && targetUser) {
        submitMutation.mutate({
          userId: targetUser.id,
          from: weekStart,
          to: weekEnd,
          emptyCount: emptyEditableCount,
        });
      }
    },
    { preventDefault: true, enableOnFormTags: true },
    [
      view,
      targetUser,
      submitMutation.isPending,
      submitMutation.mutate,
      hasSubmittableInRange,
      emptyEditableCount,
      weekStart,
      weekEnd,
    ],
  );

  return (
    <PageShell
      className={cn(
        'overflow-y-auto',
        // The indigo "viewing on behalf of" treatment is page-wide chrome
        // (border on the top edge of the shell, tint on the header block).
        viewingOnBehalfOf && 'border-t-4 border-indigo-500',
      )}
    >
      <PageShellHeaderSlot
        className={cn(
          // Match the rest of the app: no border-b, no inner padding —
          // the PageShell's p-6 already provides breathing room.
          // The indigo tint stays as an alert affordance when delegating.
          viewingOnBehalfOf && 'rounded-md bg-indigo-50/60 px-3 py-2 dark:bg-indigo-950/20',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="size-8">
            <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-0.5">
            {viewingOnBehalfOf ? (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                Acting on behalf of
              </span>
            ) : null}
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="truncate text-lg font-semibold">{displayName}</h1>
            </div>
          </div>
          {showPicker ? (
            pickerOpen ? (
              // Inline autocomplete — opens with its dropdown visible (the
              // Combobox honors `defaultOpen`) and the input focused so the
              // user can type to filter immediately. Selection or click-
              // away reverts to the link.
              <div className="ml-2 w-[260px]">
                <Combobox<User>
                  items={pickableUsers}
                  selectedItem={targetUser}
                  value={targetUser?.id ?? null}
                  defaultOpen
                  onChange={(u) => {
                    onPickUser(u);
                    setPickerOpen(false);
                  }}
                  onOpenChange={(open) => {
                    if (!open) setPickerOpen(false);
                  }}
                  getId={(u) => u.id}
                  getLabel={(u) => `${u.firstName} ${u.lastName}`.trim() || u.email}
                  filterFn={(u, q) => {
                    const hay = `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase();
                    return hay.includes(q.toLowerCase());
                  }}
                  placeholder="Search employee…"
                  className="h-8 text-sm"
                  renderItem={(u, ctx) => (
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {`${u.firstName} ${u.lastName}`.trim() || u.email}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                      {ctx.isSelected ? null : null}
                    </div>
                  )}
                />
              </div>
            ) : isDelegating && me ? (
              // In delegation mode the only header action is "Back to my
              // timesheet" — switching to another teammate is a niche flow
              // that can go through "Back" first, keeping the header clean.
              <button
                type="button"
                onClick={() => onPickUser(me)}
                className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 underline-offset-4 hover:underline dark:text-indigo-300"
              >
                <ArrowLeft className="size-3" />
                Back to my timesheet
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="ml-2 text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                Switch user
              </button>
            )
          ) : null}
        </div>
        {/* Day view is draft-entry only; submission/recall happen from week
            view and each runs as one transaction across the whole week. The
            Settings gear stays visible in both views. */}
        <div className="flex items-center gap-2">
          {showSubmitWeek ? (
            <>
              {hasRecallableSubmitted ? (
                // Per-day recall popover replaces the all-or-nothing "Recall
                // week" affordance. Mid-week submitters (Mon–Wed submitted,
                // Thu–Fri still draft) can pull back a subset; the common
                // "recall the whole week" path stays one trigger click — the
                // popover pre-selects every eligible day on open.
                <TimesheetRecallPopover
                  entries={entries}
                  weekStart={weekStart}
                  isDateLocked={isDateLocked}
                  displayName={displayUser?.firstName}
                  isDelegating={isDelegating}
                  disabled={!targetUser}
                  isPending={recallMutation.isPending}
                  onRecall={(ranges) => {
                    if (!targetUser) return;
                    recallMutation.mutate({ userId: targetUser.id, ranges });
                  }}
                  trigger={
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      disabled={!targetUser || recallMutation.isPending}
                      title="Pick days to pull back to draft."
                    >
                      {/* Confirming a recall closes the popover immediately, so
                        the popover's own "Recalling…" label never shows — the
                        trigger is the only affordance visible while the request
                        is in flight. Swap the icon for a spinner and the label
                        for an explicit progress message (the disabled state
                        alone reads as "stuck"), matching the Submit-week
                        convention. */}
                      {recallMutation.isPending ? <Loader2 className="animate-spin" /> : <Undo2 />}
                      {recallMutation.isPending
                        ? 'Recalling…'
                        : isDelegating && displayUser
                          ? `Recall ${displayUser.firstName}'s week`
                          : 'Recall week'}
                    </Button>
                  }
                />
              ) : null}
              <TimesheetSubmitButton
                partition={submitPartition}
                hasTargetUser={!!targetUser}
                isPending={submitMutation.isPending}
                isDelegating={isDelegating}
                displayFirstName={displayUser?.firstName}
                onSubmit={() => {
                  if (!targetUser) return;
                  submitMutation.mutate({
                    userId: targetUser.id,
                    from: weekStart,
                    to: weekEnd,
                    emptyCount: emptyEditableCount,
                  });
                }}
              />
            </>
          ) : null}
          <RequirePermission permission="timesheet.settings">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* `DropdownMenuTrigger asChild` clobbers this Button's own
                    `data-slot="button"` with its own `data-slot="dropdown-menu-trigger"`
                    (Radix Slot lets the outer wrapper's explicit props win), so the
                    global `[data-slot='button']` pill-radius rule never matches this
                    trigger. Set the radius directly instead of depending on that rule. */}
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="rounded-full border border-border"
                  title="More"
                  aria-label="More"
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/timesheets/settings">
                    <Settings />
                    Settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </RequirePermission>
        </div>
      </PageShellHeaderSlot>

      {/* `data-surface="panel"` opts into the shared taupe border that greys on
          hover — see the surface-card block in `styles/globals.css`. */}
      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        {/* `bg-card`, not `bg-background` — the latter is the cream page and
            would tint this track brown. `data-surface="panel"` gives it the
            shared taupe/grey border. */}
        <div
          data-surface="panel"
          data-slot="toggle-group"
          className="inline-flex items-center gap-0 rounded-md border light:bg-card p-0.5"
        >
          <ViewButton
            active={view === 'weekly'}
            onClick={() => updateParams({ view: undefined })}
            ariaLabel="Weekly view"
          >
            <Table2 />
          </ViewButton>
          <ViewButton
            active={view === 'day'}
            onClick={() => updateParams({ view: 'day' })}
            ariaLabel="Day view"
          >
            <CalendarDays />
          </ViewButton>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Previous week"
            onClick={() => updateParams({ week: addDays(weekStart, -7), day: undefined })}
          >
            <ChevronLeft />
          </Button>
          {/* div (not span) — TimesheetWeekStatusStrip mounts a block-level
              wrapper for the per-day chips, and nesting block content inside
              an inline span is invalid HTML even with display:flex. */}
          <div className="flex items-center gap-2 text-sm font-medium tabular-nums">
            {shortDate(weekStart, { withYear: true })} – {shortDate(weekEnd, { withYear: true })}
            <TimesheetWeekStatusStrip entries={entries} weekStart={weekStart} className="ml-1" />
          </div>
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Next week"
            onClick={() => updateParams({ week: addDays(weekStart, 7), day: undefined })}
          >
            <ChevronRight />
          </Button>
          {isCurrentWeek ? null : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateParams({ week: undefined, day: undefined })}
            >
              Current Week
            </Button>
          )}
        </div>
        {view === 'weekly' ? (
          // Copy / Add stay available even when every row in the week is
          // already submitted/approved — users can log additional hours or
          // rows on a submitted day without recalling it first. A fully locked
          // week is the exception: both actions would only yield read-only
          // cells, so we gate them (per-cell locks + server stay authoritative
          // for partially-locked weeks).
          <div className="ml-auto flex items-center gap-2">
            {/* Clear empty entries: only surfaces when the grid is actually
                carrying blank/zero draft cells (the clutter "Copy last week"
                leaves behind), so the toolbar stays clean on a normal week.
                Confirmed before it fires — nothing is lost but the placeholder
                cells, and an accidental sweep would still be a nuisance. */}
            {emptyEntryCount > 0 ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setClearEmptyOpen(true)}
                disabled={!targetUser || weekFullyLocked}
                title={
                  weekFullyLocked
                    ? 'This week is locked and can no longer be edited'
                    : 'Remove cells that have no hours entered'
                }
              >
                <Eraser className="size-3.5" />
                Clear {emptyEntryCount} empty entr{emptyEntryCount === 1 ? 'y' : 'ies'}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyLastWeekMutation.mutate()}
              disabled={!targetUser || copyLastWeekMutation.isPending || weekFullyLocked}
              title={
                weekFullyLocked
                  ? 'This week is locked and can no longer be edited'
                  : "Copy last week's rows into this week with hours blank"
              }
            >
              <Copy className="size-3.5" />
              {copyLastWeekMutation.isPending ? 'Copying…' : 'Copy last week'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => gridRef.current?.addRow()}
              disabled={!targetUser || weekFullyLocked}
              aria-keyshortcuts={`${MOD_KEYSHORTCUT}+Enter`}
              title={
                weekFullyLocked ? 'This week is locked and can no longer be edited' : undefined
              }
            >
              <Plus className="size-3.5" />
              Add row
              <ShortcutHint keys={MOD_ENTER_KEYS} />
            </Button>
            {/* Find: one search icon that expands a single inline bar (text +
                  status dots + stepper) on this same row — no extra line. The
                  scope lives in the URL so a rejected/approved email can
                  deep-link straight into it. */}
            {findOpen ? (
              <TimesheetWeekFindBar
                statusCounts={statusCounts}
                selectedStatuses={findStatuses}
                onToggleStatus={toggleFindStatus}
                query={findQuery}
                onQueryChange={setFindQuery}
                active={findActive}
                matchCount={findMatchCount}
                currentIndex={Math.min(findIndex, Math.max(0, findMatchCount - 1))}
                onPrev={() => stepFind(-1)}
                onNext={() => stepFind(1)}
                onClose={closeFind}
              />
            ) : (
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Find in timesheet"
                onClick={() => setFindOpen(true)}
              >
                <SearchIcon />
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {/* Lock settings drive the read-only mirror. If the fetch fails we
          can't compute which periods are locked, so cells render editable —
          surface the failure (G15) so the user understands why a save might
          still 422 on a locked period; the server stays authoritative. */}
      {settingsQuery.isError ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Couldn’t load timesheet lock settings — locked periods may not be shown as read-only.
          Saving an entry in a locked period will be rejected.
        </p>
      ) : null}

      {!targetUser && (meLoading || targetUserQuery.isLoading) ? (
        <div className="space-y-3 p-6">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : !targetUser ? (
        <p className="p-6 text-sm text-destructive">
          {isDelegating
            ? 'Could not load the requested employee. You may not have permission to view this timesheet.'
            : `No employee record is linked to ${email}. Ask an admin to add you, or check that the SSO bootstrap is enabled.`}
        </p>
      ) : (
        <>
          {/* Active-unlock hint: a locked period this week has been re-opened
              by an admin. Tells the user why previously-locked cells are now
              editable, and when the override ends. */}
          {activeUnlocks.length > 0 ? (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200">
              Some locked days this week have been unlocked until{' '}
              <span className="font-medium tabular-nums">
                {formatUnlockUntil(activeUnlocks.map((u) => u.autoEndDate))}
              </span>
              . You can edit and submit them until then.
            </div>
          ) : null}

          {view === 'weekly' ? (
            <TimesheetWeekGrid
              ref={gridRef}
              user={targetUser}
              weekStart={weekStart}
              entries={entries}
              loading={rangeQuery.isLoading}
              teamCustomers={teamCustomers}
              selectableCustomers={selectableCustomers}
              isOnTeamForDay={isOnTeamForDay}
              memberships={memberships}
              engagementVersions={engagements.versions}
              engagementsLoaded={engagements.isLoaded}
              submitFieldErrors={submitFieldErrors}
              isDateLocked={isDateLocked}
              findQuery={findQuery}
              findStatuses={findStatuses}
              findActive={findActive}
              onFindMatchCountChange={setFindMatchCount}
              onFindStatusCountsChange={setStatusCounts}
              onEmptyEntryCountChange={setEmptyEntryCount}
            />
          ) : (
            <TimesheetDayList
              ref={dayListRef}
              user={targetUser}
              weekStart={weekStart}
              day={day}
              entries={entries}
              loading={rangeQuery.isLoading}
              onPickDay={(d) => updateParams({ day: d })}
              teamCustomers={teamCustomers}
              selectableCustomers={selectableCustomers}
              isOnTeamForDay={isOnTeamForDay}
              memberships={memberships}
              engagementVersions={engagements.versions}
              engagementsLoaded={engagements.isLoaded}
              isDateLocked={isDateLocked}
            />
          )}
        </>
      )}
      {blockerDialog}
      {/* Confirm before sweeping the blank cells. They carry no hours, so nothing
          logged is lost — only the empty placeholder cells — but an accidental
          click on a busy toolbar still deserves a guard. The grid owns the
          deletion (it holds the cell grouping + manual-line state). */}
      <ConfirmDialog
        open={clearEmptyOpen}
        title={`Clear ${emptyEntryCount} empty entr${emptyEntryCount === 1 ? 'y' : 'ies'}?`}
        description={
          `${emptyEntryCount === 1 ? 'This entry has' : 'These entries have'} no hours entered. ` +
          'Clearing removes the empty draft cells; cells with any hours logged, or ' +
          'already-submitted entries, are left untouched.'
        }
        confirmLabel="Clear empty entries"
        destructive
        onConfirm={() => {
          gridRef.current?.clearEmptyEntries();
          setClearEmptyOpen(false);
        }}
        onCancel={() => setClearEmptyOpen(false)}
      />
    </PageShell>
  );
}

/** The latest auto-end date among active unlocks, formatted for the banner.
 *  Parsed as UTC midnight to dodge the local-timezone off-by-one. */
function formatUnlockUntil(autoEndDates: readonly string[]): string {
  const latest = autoEndDates.reduce((a, b) => (a > b ? a : b));
  return new Date(`${latest}T00:00:00Z`).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function ViewButton({
  active,
  onClick,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
      data-slot="toggle-group-item"
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors',
        active
          ? 'bg-muted text-foreground  light:bg-primary light:text-primary-foreground'
          : 'hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function clampToWeek(d: string, weekStart: string): string {
  const start = weekStart;
  const end = addDays(weekStart, 6);
  if (d < start) return start;
  if (d > end) return end;
  return d;
}

function stripDefaults(s: Search): Search {
  const out: Search = {};
  if (s.view && s.view !== 'weekly') out.view = s.view;
  if (s.week) out.week = s.week;
  if (s.day) out.day = s.day;
  if (s.userId) out.userId = s.userId;
  if (s.find) out.find = s.find;
  if (s.findStatus) out.findStatus = s.findStatus;
  return out;
}
